import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../hooks/useAppAuth';
import { authClient } from '../lib/auth-client';
import InitialSetup from '../components/Auth/InitialSetup';
import CodeInput from '../components/Auth/CodeInput';
import { AuthLoading, BrandMasthead, Button, Divider, Field, Notice } from '../components/Auth/AuthUi';
import {
  lastSignInMethod, passkeyOnDevice, markPasswordSignIn, notePasskeyOnDevice, rememberSignInMethod, type SignInMethod,
} from '../lib/signInSetup';

// Sign-in is two steps, as in the homeowner app. First the customer says who
// they are; then they are shown the ways to sign in that fit what they typed
// and this browser: always a password, a passkey if one has been used here,
// and an email code if they typed an email address. Nothing is looked up on
// the server before they sign in, so the page never reveals whether an
// account exists or how it is protected.

type Stage = 'identify' | 'methods' | 'code';

const SIGN_IN_TIMEOUT_MS = 20000;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_WAIT_SECONDS = 60;

function withTimeout<T>(promise: Promise<T>, timeoutMs = SIGN_IN_TIMEOUT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('Sign in is taking too long. Check your connection and try again.')), timeoutMs);
    promise.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { convexUser, isAdmin, isSignedIn, isLoaded, isSignedOut } = useBetterAuthConvexAuth();
  const hasAdmin = useQuery(api.users.hasAdmin);

  const [stage, setStage] = useState<Stage>('identify');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  // Only a wrong or used-up code marks the code box invalid; after a limit or
  // a fault the same code is kept and may still work.
  const [codeRejected, setCodeRejected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  // Read once: which method this browser used last, and whether a passkey has
  // been added or used here.
  const [lastMethod] = useState(lastSignInMethod);
  const [supportsPasskey] = useState(() => window.isSecureContext && typeof window.PublicKeyCredential !== 'undefined');
  // A passkey removed in Sign-in options is no longer offered, even if it was
  // the last method used.
  const [passkeyHere] = useState(() => supportsPasskey && (passkeyOnDevice() ?? lastMethod === 'passkey'));
  const identifierRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const firstChoiceRef = useRef<HTMLButtonElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const firstStage = useRef(true);
  // Set by "Use your password instead", so focus goes to the password box
  // rather than the method remembered from last time.
  const wantsPassword = useRef(false);

  const id = identifier.trim().toLowerCase();
  const isEmail = EMAIL_REGEX.test(id);
  // The method used last time is offered first.
  const passkeyFirst = passkeyHere && lastMethod === 'passkey';
  const emailFirst = isEmail && lastMethod === 'email' && !passkeyFirst;
  const passwordFirst = !passkeyFirst && !emailFirst;

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds(seconds => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  // Redirect if already signed in
  useEffect(() => {
    if (isSignedIn && convexUser && !isSignedOut) {
      navigate(isAdmin ? '/admin' : '/dashboard', { replace: true });
    }
  }, [isSignedIn, convexUser, isAdmin, isSignedOut, navigate]);

  // Move focus to the new step, so keyboard and screen-reader users follow along.
  useEffect(() => {
    if (firstStage.current) { firstStage.current = false; return; }
    if (stage === 'identify') identifierRef.current?.focus();
    else if (stage === 'code') codeRef.current?.focus();
    else (passwordFirst || wantsPassword.current ? passwordRef.current : firstChoiceRef.current)?.focus();
    wantsPassword.current = false;
    // Only when the step changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (!isLoaded || hasAdmin === undefined) return <AuthLoading />;
  if (hasAdmin === false) return <InitialSetup onComplete={() => window.location.reload()} />;

  const goTo = (next: Stage) => {
    setStage(next);
    setError('');
    setCodeRejected(false);
  };

  const continueToMethods = (event: React.FormEvent) => {
    event.preventDefault();
    if (!id) { setError('Enter your username or email.'); return; }
    setPassword('');
    goTo('methods');
  };

  // Every method ends the same way once Better Auth has a session: the
  // role-based route sends admins to /admin and customers to /dashboard.
  const openAccount = (used: SignInMethod) => {
    rememberSignInMethod(used);
    window.location.assign('/');
  };

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try { await action(); }
    catch (err) { setError(err instanceof Error && err.message ? err.message : 'Sign in did not work. Check your connection and try again.'); }
    finally { setBusy(false); }
  };

  const signInWithPassword = (event: React.FormEvent) => {
    event.preventDefault();
    if (!password) { setError('Enter your password.'); return; }
    return run(async () => {
      const result = await withTimeout(Promise.resolve(isEmail
        ? authClient.signIn.email({ email: id, password, rememberMe })
        : authClient.signIn.username({ username: id, password, rememberMe })));
      if (result.error) {
        throw new Error(result.error.status === 401 || result.error.status === 400
          ? "Those details aren't right. Check them and try again."
          : result.error.message || 'Sign in did not work. Try again.');
      }
      // A password sign-in is when the guided email-code setup is offered.
      markPasswordSignIn();
      openAccount('password');
    });
  };

  const signInWithPasskey = () => run(async () => {
    const result = await authClient.signIn.passkey();
    if (result.error || !result.data) {
      throw new Error(result.error?.message || 'Passkey sign-in was cancelled. Try again or use your password.');
    }
    notePasskeyOnDevice(true);
    openAccount('passkey');
  });

  const sendCode = () => run(async () => {
    const result = await withTimeout(authClient.emailOtp.sendVerificationOtp({ email: id, type: 'sign-in' }));
    if (result.error) {
      throw new Error(result.error.status === 429
        ? "We can't send another code just yet. Wait a minute, or use your password."
        : result.error.message || "We couldn't send a code. Try again or use your password.");
    }
    setCode('');
    setCodeRejected(false);
    setResendSeconds(RESEND_WAIT_SECONDS);
    if (stage !== 'code') goTo('code');
  });

  const signInWithCode = async (value: string) => {
    if (!/^\d{6}$/.test(value)) { setError('Enter the 6‑digit code from the email.'); return; }
    await run(async () => {
      const result = await withTimeout(authClient.signIn.emailOtp({ email: id, otp: value }));
      const { error: failure } = result;
      if (failure) {
        // Only a wrong or used-up code is cleared; after a limit or a fault the
        // same code may still work.
        if (failure.status === 429) throw new Error('Too many tries. Wait a few minutes, or use your password instead.');
        if (failure.code === 'OTP_EXPIRED' || failure.code === 'TOO_MANY_ATTEMPTS') {
          setCode('');
          setCodeRejected(true);
          throw new Error('That code has stopped working. Send a new code.');
        }
        if (failure.status && failure.status >= 500) throw new Error('Something went wrong on our side. Try again in a moment.');
        setCode('');
        setCodeRejected(true);
        throw new Error("That code isn't right. Check the email and try again.");
      }
      openAccount('email');
    });
    // Still here, so it did not work: put the customer back in the code box.
    window.requestAnimationFrame(() => codeRef.current?.focus());
  };

  const errorNotice = error ? <Notice tone="danger" id="login-error">{error}</Notice> : null;
  const describedByError = error ? 'login-error' : undefined;

  // A passkey picks its own account, whatever was typed above it.
  const passkeyButton = (primary: boolean) => <div className="space-y-1.5">
    <Button ref={primary ? firstChoiceRef : undefined} icon="fingerprint" aria-describedby="login-passkey-note"
      variant={primary ? 'primary' : 'secondary'} onClick={() => void signInWithPasskey()} loading={primary && busy} disabled={busy}>
      Sign in with a passkey
    </Button>
    <p id="login-passkey-note" className="text-center text-[13px] text-asg-slate">Signs in to the account your passkey belongs to.</p>
  </div>;

  const emailCodeButton = (primary: boolean) => <Button ref={primary ? firstChoiceRef : undefined} icon="mail"
    variant={primary ? 'primary' : 'secondary'} onClick={() => void sendCode()} loading={primary && busy} disabled={busy}>
    Email me a sign-in code
  </Button>;

  const signingInAs = <div className="flex items-center gap-3">
    <div className="min-w-0 flex-1">
      <p className="text-[13px] text-asg-slate">Signing in as</p>
      <p className="truncate text-[17px] font-semibold text-asg-ink">{id}</p>
    </div>
    <Button variant="quiet" className="!w-auto !px-3" disabled={busy} onClick={() => goTo('identify')}>
      Change<span className="sr-only"> who is signing in</span>
    </Button>
  </div>;

  return <div className="flex min-h-screen flex-col bg-asg-paper text-asg-ink">
    <main className="mx-auto flex w-full max-w-[560px] flex-1 flex-col sm:px-6 sm:pt-10">
      <BrandMasthead title="See what your roof made today" subtitle="Daily readings from your solar panels." />

      <div className="space-y-4 px-6 py-6 sm:px-0">
        {stage === 'identify' && <form noValidate onSubmit={continueToMethods} className="space-y-4">
          <Field ref={identifierRef} id="login-identifier" label="Username or email" value={identifier}
            onChange={event => { setIdentifier(event.target.value); if (error) setError(''); }}
            type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            invalid={!!error} describedBy={describedByError} hint="For example john.smith or john@example.co.uk" />
          {errorNotice}
          <Button type="submit">Continue</Button>
          {supportsPasskey && <Button icon="fingerprint" variant={passkeyHere ? 'secondary' : 'quiet'}
            onClick={() => void signInWithPasskey()} loading={busy} disabled={busy}>
            Sign in with a passkey
          </Button>}
        </form>}

        {stage === 'methods' && <div className="space-y-4">
          {signingInAs}
          {passkeyFirst && <>{passkeyButton(true)}<Divider label="or use your password" /></>}
          {emailFirst && <>{emailCodeButton(true)}<Divider label="or use your password" /></>}

          <form noValidate onSubmit={signInWithPassword} className="space-y-4">
            {/* Lets password managers match the saved login to this form. */}
            <input type="text" name="username" autoComplete="username" value={id} readOnly hidden />
            <Field ref={passwordRef} id="login-password" label="Password" secure value={password}
              onChange={event => { setPassword(event.target.value); if (error) setError(''); }}
              autoComplete="current-password" invalid={!!error && !busy} describedBy={describedByError} />
            <label className="flex items-center gap-3 text-[15px] text-asg-ink">
              <input type="checkbox" checked={rememberMe} onChange={event => setRememberMe(event.target.checked)}
                className="h-5 w-5 rounded accent-asg-forest" />
              Keep me signed in on this device
            </label>
            {errorNotice}
            <Button type="submit" variant={passwordFirst ? 'primary' : 'secondary'} loading={passwordFirst && busy} disabled={busy}>
              Sign in
            </Button>
          </form>

          {((passkeyHere && !passkeyFirst) || (isEmail && !emailFirst)) && <Divider label="or" />}
          {passkeyHere && !passkeyFirst && passkeyButton(false)}
          {isEmail && !emailFirst && emailCodeButton(false)}
        </div>}

        {stage === 'code' && <form noValidate onSubmit={event => { event.preventDefault(); void signInWithCode(code); }} className="space-y-4">
          {signingInAs}
          <Notice tone="info">
            If email codes are turned on for this address, a 6‑digit code is on its way. It lasts 5 minutes. Check your junk folder too.
            <span className="mt-2 block">Not turned them on yet? Use your password this time, and we'll help you set them up.</span>
          </Notice>
          <CodeInput ref={codeRef} id="login-code" label="Email code" value={code} disabled={busy}
            onChange={value => { setCode(value); setCodeRejected(false); if (error) setError(''); }} onComplete={value => void signInWithCode(value)}
            invalid={codeRejected} describedBy={describedByError} />
          {errorNotice}
          <Button type="submit" loading={busy} disabled={code.length !== 6}>Sign in</Button>
          <Button variant="quiet" disabled={busy || resendSeconds > 0} onClick={() => void sendCode()}>
            {resendSeconds > 0 ? `Send a new code in ${resendSeconds}s` : 'Send a new code'}
          </Button>
          <Button variant="quiet" disabled={busy} onClick={() => { wantsPassword.current = true; goTo('methods'); }}>Use your password instead</Button>
        </form>}

        <p className="pt-2 text-center text-[15px] text-asg-slate">
          Can't sign in?{' '}
          <a href="https://www.ashadegreener.co.uk/contact-solar" target="_blank" rel="noopener noreferrer"
            className="font-semibold text-asg-forest underline">Contact us</a>
        </p>
      </div>
    </main>

    <footer className="px-6 pb-6 pt-2 text-center text-[13px] leading-[18px] text-asg-slate">
      <p>
        By signing in you agree to our{' '}
        <Link to="/terms" className="font-semibold text-asg-forest underline">terms</Link> and{' '}
        <a href="https://www.ashadegreener.co.uk/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-asg-forest underline">privacy policy</a>.
      </p>
      <p className="mt-1">Sterling House, Maple Court, Tankersley, S75 3DP</p>
    </footer>
  </div>;
};

export default LoginPage;
